import { GroupSummary } from './GenerateGroupSummary';

export type GroupingFlow = 'GROUP' | 'SPLIT' | 'CUSTOM' | 'CONVERT' | '';

/**
 * Which shipment-options flow a selection of items calls for.
 *
 * Extracted from a useMemo inside GroupingPanel so it can be tested directly, because two flows depend
 * on its exact shape for their arithmetic to be correct rather than merely reasonable:
 *
 *   - SPLIT replicates: every box declares a COPY of every row, with the value shared between them.
 *     That is right for one unit going into several boxes and wrong for anything else - a quantity-3 row
 *     replicated into 2 boxes declares 6 units. It is safe only because SPLIT requires totalQuantity === 1.
 *   - CONVERT divides: it distributes the units themselves, with per-unit value and weight. That is the
 *     flow a quantity greater than one belongs in, and the CONVERT test below sits ahead of SPLIT so it
 *     wins.
 *
 * Order matters, so the tests pin it. Widening SPLIT to admit a larger quantity would silently
 * over-declare a customs line, which is why that case is asserted rather than assumed.
 *
 * `splittingEnabled` is passed in rather than read from SettingsCache, so this stays pure.
 */
export function ResolveGroupingFlow(summary: GroupSummary, splittingEnabled: boolean): GroupingFlow {
  if (summary.totalOrders === 0) {
    return '';
  }

  if (splittingEnabled) {
    if (summary.totalOrders === 1 && summary.smartSendItemCount === 1 && summary.totalQuantity > 1) {
      return 'CONVERT';
    }
    if (summary.totalOrders === 1 && summary.totalQuantity === 1) {
      return 'SPLIT';
    }
    if (summary.smartSendItemCount === 1 && summary.totalOrders > 1) {
      return 'CUSTOM';
    }
    if (summary.smartSendItemCount > 1 && summary.totalOrders > 2) {
      return 'GROUP';
    }
  }

  return summary.smartSendItemCount > 1 ? 'GROUP' : '';
}
